import { ExportFileConfig } from 'src/utils/exportFileUtils';
import {
  TableProps,
  StatusIndicator,
  Button,
  Popover,
} from '@cloudscape-design/components';
import { Tag } from 'src/types/helios/Tags';
import { BusinessUnit } from 'src/types/orbit/BusinessUnit';
import { FILTER_OPERATORS } from 'src/contstant/FilterTypes';
import { FilterItemType } from 'src/hooks/useGenericFilter';
import { createDateFilterConfig } from 'src/components/filter/FilterDatePicker';
import { formatDateTime } from 'src/utils/generalUtils';
import React from 'react';

interface TagValueItem {
  key: string;
  label: string;
}

interface TagValuesCellProps {
  item: Tag;
}

// Constants
const TAG_VALUES_CONSTANTS = {
  EMPTY_DISPLAY: '-',
  MORE_BUTTON_TEXT: (count: number) => `+${count} more`,
} as const;

const generateSafeKey = (prefix: string, index: number): string => {
  return `${prefix}-${index}`.replace(/[^a-zA-Z0-9-]/g, '-');
};

export const TagValuesCell = ({ item }: TagValuesCellProps): React.ReactElement => {
  if (!item.values || Object.keys(item.values).length === 0) {
    return <span>{TAG_VALUES_CONSTANTS.EMPTY_DISPLAY}</span>;
  }

  const activeValues: TagValueItem[] = Object.entries(item.values)
    .filter(([, value]) => value.isActive)
    .map(([key], index) => ({
      key: generateSafeKey(item.tagId, index),
      label: key,
    }));

  if (activeValues.length === 0) return <span>{TAG_VALUES_CONSTANTS.EMPTY_DISPLAY}</span>;

  const remainingCount = activeValues.length - 3;

  return (
    <div className="tag-container">
      {activeValues.slice(0, 3).map(({ key, label }) => (
        <span key={key} className="tag">
          {label}
        </span>
      ))}
      {remainingCount > 0 && (
        <Popover
          size="medium"
          position="top"
          triggerType="custom"
          content={
            <div className="tag-popover-container">
              {activeValues.map(({ key, label }) => (
                <span key={key} className="tag">
                  {label}
                </span>
              ))}
            </div>
          }
        >
          <Button variant="inline-link">
            {TAG_VALUES_CONSTANTS.MORE_BUTTON_TEXT(remainingCount)}
          </Button>
        </Popover>
      )}
    </div>
  );
};

const enum TagPropertyKey {
  BUSINESS_UNIT = 'parent.businessUnit',
  TAG_ID = 'tagId',
  ACTIVE_STATUS = 'parent.activeStatus',
  LAST_UPDATED_AT = 'parent.LastUpdatedAt',
  CREATED_AT = 'parent.createdAt',
}
export const initialFilters = [
  {
    propertyKey: TagPropertyKey.ACTIVE_STATUS,
    value: 'true',
    label: 'Active',
  },
  {
    propertyKey: TagPropertyKey.ACTIVE_STATUS,
    value: 'false',
    label: 'Inactive',
  },
];

export const getItemValue = (item: Tag, propertyKey: string): any => {
  if (!item) return '';

  switch (propertyKey) {
    case TagPropertyKey.BUSINESS_UNIT:
      return {
        itemValue: item.businessUnit,
        type: FilterItemType.TEXT,
      };
    case TagPropertyKey.TAG_ID:
      return {
        itemValue: item.tagGroupName,
        type: FilterItemType.TEXT,
      };
    case TagPropertyKey.ACTIVE_STATUS:
      return {
        itemValue: item.isActive,
        type: FilterItemType.BOOLEAN,
      };
    case TagPropertyKey.LAST_UPDATED_AT:
      return {
        itemValue: item.lastUpdatedAt,
        type: FilterItemType.DATE,
      };
    case TagPropertyKey.CREATED_AT:
      return {
        itemValue: item.createdAt,
        type: FilterItemType.DATE,
      };
    default:
      return { itemValue: '' };
  }
};

export const getSearchableFields = (item: Tag) => [
  item.tagGroupName || '',
  item.businessUnit || '',
  item.description || '',
  Object.keys(item.values).join(' ') || '',
  item.createdBy || '',
  item.lastUpdatedBy || '',
];

export const getFilterTagsProperties = (isPlatformAdmin: boolean): any[] => {
  const baseFilters: any[] = [
    {
      key: TagPropertyKey.TAG_ID,
      propertyLabel: 'Tag Group',
      operators: [FILTER_OPERATORS.EQUALS, FILTER_OPERATORS.NOT_EQUALS],
      groupValuesLabel: 'Tag Group values',
    },
    {
      key: TagPropertyKey.ACTIVE_STATUS,
      propertyLabel: 'Status',
      operators: [FILTER_OPERATORS.EQUALS, FILTER_OPERATORS.NOT_EQUALS],
      groupValuesLabel: 'Status values',
    },

    createDateFilterConfig({
      key: TagPropertyKey.CREATED_AT,
      propertyLabel: 'Created At',
      groupValuesLabel: 'Date',
    }),
    createDateFilterConfig({
      key: TagPropertyKey.LAST_UPDATED_AT,
      propertyLabel: 'Last Updated At',
      groupValuesLabel: 'Date',
    }),
  ];

  if (isPlatformAdmin) {
    baseFilters.push({
      key: TagPropertyKey.BUSINESS_UNIT,
      propertyLabel: 'Business Unit',
      operators: [FILTER_OPERATORS.EQUALS, FILTER_OPERATORS.NOT_EQUALS],
      groupValuesLabel: 'Business Unit values',
    });
  }

  return baseFilters;
};

export const getTagsTableDefinitions = (
  businessUnitList: BusinessUnit[] = [],
): TableProps.ColumnDefinition<Tag>[] => {
  const getBusinessUnitName = (businessUnitId: string) => {
    const businessUnit = businessUnitList.find(
      (bu) => bu.businessUnitId === businessUnitId,
    );
    return businessUnit ? businessUnit.name : businessUnitId;
  };

  return [
    {
      id: 'tagId',
      header: 'Tag Group',
      cell: (item: Tag) => item.tagGroupName || '-',
      isRowHeader: true,
    },
    {
      id: 'businessUnitId',
      header: 'Business Unit',
      cell: (item: Tag) => getBusinessUnitName(item.businessUnit) || '-',
    },
    {
      id: 'description',
      header: 'Description',
      cell: (item: Tag) => item.description || '-',
    },
    {
      id: 'values',
      header: 'Tag Values (Active)',
      cell: (item: Tag) => <TagValuesCell item={item} />,
      maxWidth: 300,
    },
    {
      id: 'isActive',
      header: 'Status',
      cell: (item: Tag) => (
        <StatusIndicator type={item.isActive ? 'success' : 'error'}>
          {item.isActive ? 'Active' : 'Inactive'}
        </StatusIndicator>
      ),
    },
    {
      id: 'isMandatoryForQuestions',
      header: 'Mandatory For Questions',
      cell: (item: Tag) => (item.isMandatoryForQuestions ? 'Yes' : 'No'),
    },
    {
      id: 'isMandatoryForAuditTemplates',
      header: 'Mandatory For Audit Templates',
      cell: (item: Tag) => (item.isMandatoryForAuditTemplates ? 'Yes' : 'No'),
    },

    {
      id: 'maxTagsPerTemplate',
      header: 'Max Tags Per Template',
      cell: (item: Tag) => item.maxTagsPerTemplate?.toString() || '-',
    },
    {
      id: 'createdAt',
      header: 'Created At',
      cell: (item: Tag) => (item.createdAt ? formatDateTime(item.createdAt) : '-'),
    },
    {
      id: 'createdBy',
      header: 'Created By',
      cell: (item: Tag) => item.createdBy || '-',
    },
    {
      id: 'lastUpdatedAt',
      header: 'Last Updated At',
      cell: (item: Tag) =>
        item.lastUpdatedAt ? formatDateTime(item.lastUpdatedAt) : '-',
    },
    {
      id: 'lastUpdatedBy',
      header: 'Last Updated By',
      cell: (item: Tag) => item.lastUpdatedBy || '-',
    },
  ];
};

export const contentDisplayPreference = {
  title: 'Column preferences',
  description: 'Customize which columns are shown',
  options: [
    { id: 'tagId', label: 'Tag Group' },
    { id: 'businessUnitId', label: 'Business Unit' },
    { id: 'description', label: 'Description' },
    { id: 'values', label: 'Values' },
    { id: 'isActive', label: 'Status' },
    { id: 'isMandatoryForQuestions', label: 'Mandatory For Questions' },
    { id: 'isMandatoryForAuditTemplates', label: 'Mandatory For Audit Templates' },

    { id: 'maxTagsPerTemplate', label: 'Max Tags Per Template' },
    { id: 'createdAt', label: 'Created At' },
    { id: 'createdBy', label: 'Created By' },
    { id: 'lastUpdatedAt', label: 'Last Updated At' },
    { id: 'lastUpdatedBy', label: 'Last Updated By' },
  ],
};

export const contentDisplay = [
  { id: 'tagId', visible: true },
  { id: 'businessUnitId', visible: true },
  { id: 'description', visible: true },
  { id: 'values', visible: true },
  { id: 'isActive', visible: true },
  { id: 'isMandatoryForQuestions', visible: false },
  { id: 'isMandatoryForAuditTemplates', visible: false },

  { id: 'maxTagsPerTemplate', visible: false },
  { id: 'createdAt', visible: false },
  { id: 'createdBy', visible: false },
  { id: 'lastUpdatedAt', visible: true },
  { id: 'lastUpdatedBy', visible: false },
];

export const pageSizePreference = {
  title: 'Page size preferences',
  options: [
    { value: 10, label: '10 items' },
    { value: 20, label: '20 items' },
    { value: 30, label: '30 items' },
    { value: 50, label: '50 items' },
    { value: 100, label: '100 items' },
  ],
};

// Get predefined filter options for tags
export const getTagFilterOptions = (tagsList: Tag[] = []) => {
  // Get unique tag group names from the current tags list
  const uniqueTagNames = new Set(tagsList.map((tag) => tag.tagGroupName).filter(Boolean));
  const tagIdOptions = Array.from(uniqueTagNames).map((tagName) => ({
    propertyKey: TagPropertyKey.TAG_ID,
    value: tagName,
    label: tagName,
  }));

  return tagIdOptions.concat(initialFilters);
};

export const getExportTagsConfig = (
  businessUnitList: BusinessUnit[] = [],
): ExportFileConfig => {
  const getBusinessUnitName = (businessUnitId: string) => {
    const businessUnit = businessUnitList.find(
      (bu) => bu.businessUnitId === businessUnitId,
    );
    return businessUnit ? businessUnit.name : businessUnitId;
  };

  return {
    fileName: 'tags_list.csv',
    mappings: {
      'Tag Group': (tag: Tag) => tag.tagGroupName || '',
      'Business Unit': (tag: Tag) => getBusinessUnitName(tag.businessUnit),
      'Description': 'description',
      'Tag Values': (tag: Tag) => {
        if (!tag.values || Object.keys(tag.values).length === 0) return '';
        return Object.entries(tag.values)
          .map(
            ([key, value]) =>
              `${key}: ${value.description} (${value.isActive ? 'Active' : 'Inactive'})`,
          )
          .join('; ');
      },
      'Tag Values Keys': (tag: Tag) => {
        if (!tag.values) return '';
        return Object.keys(tag.values).join('; ');
      },
      'Tag Status': (tag: Tag) => (tag.isActive ? 'Active' : 'Inactive'),
      'Mandatory For Questions': (tag: Tag) =>
        tag.isMandatoryForQuestions ? 'Yes' : 'No',
      'Mandatory for Audit Templates': (tag: Tag) =>
        tag.isMandatoryForAuditTemplates ? 'Yes' : 'No',

      'Max Tags Per Template': (tag: Tag) => tag.maxTagsPerTemplate?.toString() || '0',
      'Created At': (tag: Tag) => (tag.createdAt ? formatDateTime(tag.createdAt) : '-'),
      'Created By': 'createdBy',
      'Last Updated At': (tag: Tag) =>
        tag.lastUpdatedAt ? formatDateTime(tag.lastUpdatedAt) : '-',
      'Last Updated By': 'lastUpdatedBy',
    },
  };
};
